'use client';

import { useState, useMemo } from 'react';
import { Search, Plus, ChevronRight, ChevronDown, Edit2, Folder, FileText } from 'lucide-react';
import { PageHeader } from '../../../components/shared/PageHeader';
import { Modal, FormField, FormInput, FormSelect, FormTextarea, Button } from '../../../components/shared/Modal';
import { accounts } from '../data/mock-data';
import { formatCurrency } from '../../../types';
import { accountTypeConfig, type AccountType } from '../types';
import type { Account } from '../types';

// 계정 추가 모달
function AccountAddModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="계정 추가"
      description="새로운 계정과목을 등록합니다."
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>취소</Button>
          <Button onClick={onClose}>등록</Button>
        </>
      }
    >
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <FormField label="계정코드" required>
            <FormInput placeholder="예: 1010" />
          </FormField>
          <FormField label="계정명" required>
            <FormInput placeholder="계정명을 입력하세요" />
          </FormField>
          <FormField label="계정 유형" required>
            <FormSelect>
              <option value="">선택하세요</option>
              {Object.entries(accountTypeConfig).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </FormSelect>
          </FormField>
          <FormField label="상위 계정">
            <FormSelect>
              <option value="">없음 (최상위 계정)</option>
              {accounts.filter(a => a.isGroup).map((account) => (
                <option key={account.id} value={account.id}>{account.code} - {account.name}</option>
              ))}
            </FormSelect>
          </FormField>
          <FormField label="계정 구분" required>
            <FormSelect>
              <option value="group">그룹 (하위 계정 포함)</option>
              <option value="ledger">원장 계정</option>
            </FormSelect>
          </FormField>
          <FormField label="초기 잔액">
            <FormInput type="number" min={0} placeholder="0" />
          </FormField>
        </div>
        <FormField label="설명">
          <FormTextarea rows={3} placeholder="계정과목에 대한 설명을 입력하세요" />
        </FormField>
      </div>
    </Modal>
  );
}

export function ChartOfAccountsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['ACC001', 'ACC002', 'ACC008', 'ACC020', 'ACC021', 'ACC025', 'ACC030', 'ACC040', 'ACC050']));
  const [isModalOpen, setIsModalOpen] = useState(false);

  // 계정과목을 트리 구조로 변환
  const accountTree = useMemo(() => {
    const rootAccounts = accounts.filter((a) => !a.parentId);

    const buildTree = (parentId?: string): Account[] => {
      return accounts
        .filter((a) => a.parentId === parentId)
        .map((account) => ({
          ...account,
          children: buildTree(account.id),
        }));
    };

    return rootAccounts.map((account) => ({
      ...account,
      children: buildTree(account.id),
    }));
  }, []);

  // 필터링된 계정 (검색 및 유형)
  const filteredAccounts = useMemo(() => {
    return accounts.filter((account) => {
      const matchesSearch =
        account.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        account.name.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesType =
        selectedType === 'all' || account.accountType === selectedType;

      return matchesSearch && matchesType;
    });
  }, [searchQuery, selectedType]);

  const toggleGroup = (id: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedGroups(newExpanded);
  };

  // 재귀적으로 계정 렌더링
  const renderAccount = (account: Account & { children?: Account[] }, level: number = 0) => {
    const isExpanded = expandedGroups.has(account.id);
    const hasChildren = account.children && account.children.length > 0;
    const config = accountTypeConfig[account.accountType];

    const colorClasses: Record<string, string> = {
      blue: 'text-blue-600 dark:text-blue-400',
      red: 'text-red-600 dark:text-red-400',
      purple: 'text-purple-600 dark:text-purple-400',
      green: 'text-green-600 dark:text-green-400',
      orange: 'text-orange-600 dark:text-orange-400',
    };

    const bgColorClasses: Record<string, string> = {
      blue: 'bg-blue-100 dark:bg-blue-900/30',
      red: 'bg-red-100 dark:bg-red-900/30',
      purple: 'bg-purple-100 dark:bg-purple-900/30',
      green: 'bg-green-100 dark:bg-green-900/30',
      orange: 'bg-orange-100 dark:bg-orange-900/30',
    };

    // 검색 필터가 있을 때 이 계정이 결과에 포함되는지 확인
    if (searchQuery || selectedType !== 'all') {
      const isInFilter = filteredAccounts.some((a) => a.id === account.id);
      const hasFilteredChildren = account.children?.some((child) =>
        filteredAccounts.some((a) => a.id === child.id)
      );

      if (!isInFilter && !hasFilteredChildren) {
        return null;
      }
    }

    return (
      <div key={account.id}>
        <div
          className={`flex items-center gap-2 px-3 py-2 hover:bg-accent/50 rounded-lg cursor-pointer ${
            account.isGroup ? 'font-medium' : ''
          }`}
          style={{ paddingLeft: `${level * 24 + 12}px` }}
          onClick={() => hasChildren && toggleGroup(account.id)}
        >
          {/* 확장/축소 아이콘 */}
          <div className="w-5 h-5 flex items-center justify-center">
            {hasChildren ? (
              isExpanded ? (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              )
            ) : null}
          </div>

          {/* 폴더/파일 아이콘 */}
          <div className={`w-6 h-6 rounded flex items-center justify-center ${bgColorClasses[config.color]}`}>
            {account.isGroup ? (
              <Folder className={`w-3.5 h-3.5 ${colorClasses[config.color]}`} />
            ) : (
              <FileText className={`w-3.5 h-3.5 ${colorClasses[config.color]}`} />
            )}
          </div>

          {/* 계정 코드 */}
          <span className="font-mono text-sm text-muted-foreground w-16">
            {account.code}
          </span>

          {/* 계정명 */}
          <span className="flex-1">{account.name}</span>

          {/* 계정 유형 */}
          <span className={`px-2 py-0.5 rounded text-xs ${bgColorClasses[config.color]} ${colorClasses[config.color]}`}>
            {config.label}
          </span>

          {/* 잔액 (그룹이 아닌 경우만) */}
          {!account.isGroup && (
            <span className="font-mono text-sm w-32 text-right">
              {formatCurrency(account.balance)}
            </span>
          )}

          {/* 수정 버튼 */}
          <button
            className="p-1 rounded hover:bg-accent opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={(e) => {
              e.stopPropagation();
              // 수정 로직
            }}
          >
            <Edit2 className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        {/* 하위 계정 */}
        {hasChildren && isExpanded && (
          <div>
            {account.children!.map((child) =>
              renderAccount(child as Account & { children?: Account[] }, level + 1)
            )}
          </div>
        )}
      </div>
    );
  };

  // 유형별 통계
  const typeStats = useMemo(() => {
    const stats: Record<AccountType, { count: number; balance: number }> = {
      asset: { count: 0, balance: 0 },
      liability: { count: 0, balance: 0 },
      equity: { count: 0, balance: 0 },
      income: { count: 0, balance: 0 },
      expense: { count: 0, balance: 0 },
    };

    accounts.forEach((account) => {
      if (!account.isGroup) {
        stats[account.accountType].count++;
        stats[account.accountType].balance += account.balance;
      }
    });

    return stats;
  }, []);

  return (
    <div className="space-y-6">
      <PageHeader
        title="계정과목표"
        description="계정과목을 조회하고 관리합니다."
        actions={
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="w-4 h-4" />
            계정 추가
          </button>
        }
      />

      {/* 유형별 요약 카드 - 모바일에서 스크롤 가능 */}
      <div className="overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0 scrollbar-none">
        <div className="grid grid-cols-5 gap-2 sm:gap-3 min-w-[500px] sm:min-w-0">
          {(Object.entries(accountTypeConfig) as [AccountType, { label: string; color: string }][]).map(
            ([type, config]) => {
              const colorClasses: Record<string, string> = {
                blue: 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/10',
                red: 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/10',
                purple: 'border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/10',
                green: 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/10',
                orange: 'border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/10',
              };
              return (
                <button
                  key={type}
                  onClick={() => setSelectedType(selectedType === type ? 'all' : type)}
                  className={`p-2 sm:p-3 rounded-lg border-2 text-left transition-colors ${
                    selectedType === type
                      ? 'border-primary'
                      : colorClasses[config.color]
                  }`}
                >
                  <div className="text-xs text-muted-foreground whitespace-nowrap">{config.label}</div>
                  <div className="text-base sm:text-lg font-bold mt-0.5 sm:mt-1">
                    {typeStats[type].count}개
                  </div>
                  <div className="text-[10px] sm:text-xs text-muted-foreground truncate">
                    {formatCurrency(typeStats[type].balance)}
                  </div>
                </button>
              );
            }
          )}
        </div>
      </div>

      {/* 검색 */}
      <div className="relative w-full sm:max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="계정코드, 계정명 검색..."
          className="w-full h-9 sm:h-10 pl-10 pr-4 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      {/* 계정과목 트리 - 스크롤 가능 */}
      <div className="rounded-lg bg-card border border-border overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent">
          <div className="min-w-[600px] p-2 sm:p-4">
            <div className="flex items-center gap-2 px-3 py-2 border-b border-border mb-2 text-xs sm:text-sm font-medium text-muted-foreground">
              <div className="w-5 h-5" />
              <div className="w-6 h-6" />
              <span className="w-14 sm:w-16">코드</span>
              <span className="flex-1">계정명</span>
              <span className="w-14 sm:w-16">유형</span>
              <span className="w-28 sm:w-32 text-right">잔액</span>
              <div className="w-8" />
            </div>

            <div className="space-y-0.5">
              {accountTree.map((account) =>
                renderAccount(account as Account & { children?: Account[] })
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 계정 추가 모달 */}
      <AccountAddModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}
